// client/src/features/tenants/search.ts — SCR-TEN-001's URL state · TEN-004 (04 §9.2.3).
//
// §9.2.3 is NORMATIVE for every LIST screen: filters, pagination and sort live in the URL as
// Zod-validated search params, so a filtered list is shareable, survives refresh, and browser
// Back is a filter-undo. `features/properties/search.ts` is the shape this copies.
//
// The schema lives here rather than in the route file because two things need it: the route
// (as `validateSearch`) and the list component (as the type of what it reads back).

import { z } from 'zod';

import { TAXPAYER_TYPES } from './enums';

/** 02 §7.4 — `pageSize` default 25, max 200; the server refuses more rather than clamping. */
export const tenantSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  sort: z.string().optional(),
  q: z.string().trim().optional(),
  taxpayerType: z.enum(TAXPAYER_TYPES).optional(),
  /**
   * "Doar chiriașii activi". A boolean rather than a status enum because `ContractStatus` is
   * CON-002's canon and this screen has no business naming its members before it exists — the
   * question a landlord asks of a tenant list is "who is renting from me right now".
   */
  hasActiveContract: z.coerce.boolean().optional(),
});

export type TenantSearch = z.infer<typeof tenantSearchSchema>;

/** Which filters are engaged — the empty state needs to know why it is empty. */
export function hasActiveFilters(search: TenantSearch): boolean {
  return (
    (search.q !== undefined && search.q !== '') ||
    search.taxpayerType !== undefined ||
    search.hasActiveContract === true
  );
}

/** Clearing filters returns to page 1: page 3 of a set that no longer exists renders nothing. */
export const CLEARED_SEARCH: TenantSearch = { page: 1, pageSize: 25 };
